import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { add } from 'date-fns';
import { Alert, Checkbox, ErrorText, FieldGroup, Modal, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { client } from 'shared';
import { useUserContext } from 'UserContext';
import { IJobPaymentError } from 'models';
import JobPaymentErrorDisplay from 'parent-portal/shared/JobPaymentErrorDisplay';
import { tandemRecommendedDescription } from 'parent-portal/jobs/postJob/recommendedDescription';
import {
    uncoveredVpkFeeConfirmLabel,
    uncoveredVpkFeeNotice,
    vpkFeePercent,
} from 'parent-portal/jobs/postJob/payScales/vpkClassroomQuestion';
import { useCoveredShifts } from 'parent-portal/shared/CoveredShifts/useCoveredShifts';
import {
    findShiftTimesProblem,
    isCompleteDate,
    MINIMUM_SHIFT_MINUTES,
    useScheduleFields,
} from 'parent-portal/jobs/postJob/hooks/useScheduleFields';
import { useRequestedWorkers } from 'parent-portal/jobs/postJob/hooks/useRequestedWorkers';
import { CoverageDateFields } from 'parent-portal/jobs/postJob/components/CoverageDateFields';
import { DaysTimesFields } from 'parent-portal/jobs/postJob/components/DaysTimesFields';
import { RequestedWorkerFields } from 'parent-portal/jobs/postJob/components/RequestedWorkerFields';
import { buildTimesOfDay, jobRequestErrorMessage } from 'parent-portal/jobs/postJob/postJobApi';
import type { RequestedWorker } from 'parent-portal/jobs/postJob/formTypes';
import useClearinghouseVerification from 'parent-portal/shared/FloridaClearinghouse/useClearinghouseVerification';

function AutoHireWorkerDialogContent({
    setOpen,
    addressId,
    defaultDay,
    refresh,
}: {
    setOpen: (open: boolean) => void;
    addressId?: number;
    defaultDay?: Date;
    refresh: () => void;
}) {
    const { user } = useUserContext();
    const [presets] = useState(() => {
        const day = moment(defaultDay ?? add(new Date(), { days: 1 })).format('YYYY-MM-DD');
        return { startDate: day, endDate: day };
    });
    const schedule = useScheduleFields(presets);

    const addresses = useMemo(() => user?.address ?? [], [user]);
    const address = useMemo(
        () =>
            addresses.find((entry) => entry.id === addressId) ??
            addresses.find((entry) => entry.address_type === 'PRIMARY') ??
            addresses[0],
        [addresses, addressId],
    );
    const businessLocation = address?.business_location ?? addresses[0]?.business_location;
    const selectedAddressIds = useMemo(() => (address ? [address.id] : []), [address]);

    const workers = useRequestedWorkers({
        presets: undefined,
        addresses,
        selectedAddressIds,
        startDate: schedule.startDate,
        endDate: schedule.endDate,
        selectedDays: schedule.selectedDays,
        times: schedule.times,
    });
    const coveredShifts = useCoveredShifts(schedule.startDate, schedule.endDate, selectedAddressIds);

    const [selectedWorker, setSelectedWorker] = useState<RequestedWorker | null>(null);
    const [isVpkClassroom, setIsVpkClassroom] = useState(false);
    const [uncoveredFeeAcknowledged, setUncoveredFeeAcknowledged] = useState(false);
    const [confirmSelected, setConfirmSelected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentError, setPaymentError] = useState<IJobPaymentError>();

    const isVpkLocation = !!businessLocation?.is_vpk_location;
    const showUncoveredFeeNotice =
        isVpkLocation && !!businessLocation?.requires_vpk_classroom_confirmation && !isVpkClassroom;
    const feePercent = vpkFeePercent(user?.businesses_active);

    const toggleWorker = (worker: RequestedWorker) => {
        setSelectedWorker((current) =>
            current && current.id === worker.id && current.spotlight === worker.spotlight ? null : worker,
        );
    };

    const daysWithinRange = schedule.selectedDays.filter((day) => schedule.availableDays.includes(day));
    const timesProblem = findShiftTimesProblem(
        schedule.startDate,
        daysWithinRange,
        schedule.timesMode,
        schedule.times,
        schedule.perDayTimes,
    );

    const disabledReason = (() => {
        if (!isCompleteDate(schedule.startDate) || !isCompleteDate(schedule.endDate)) {
            return 'Pick a start and end date.';
        }
        if (moment(schedule.startDate).isBefore(moment().startOf('day'))) return 'The start date is in the past.';
        if (moment(schedule.endDate).diff(moment(schedule.startDate), 'days') > 90) {
            return 'The end date must be within 90 days of the start date.';
        }
        if (daysWithinRange.length === 0) return 'Select at least one day within the date range.';
        if (timesProblem === 'incomplete') return 'Enter valid shift times.';
        if (timesProblem === 'too_short') return `Shifts must be at least ${MINIMUM_SHIFT_MINUTES} minutes.`;
        if (timesProblem === 'start_in_past') return "Today's start time has already passed.";
        if (!selectedWorker) return 'Select the worker to schedule.';
        if (showUncoveredFeeNotice && !uncoveredFeeAcknowledged) return 'Confirm the VPK fee acknowledgement.';
        if (!confirmSelected) return 'Confirm the worker has agreed to these times.';
        return null;
    })();

    const { submitWithVerification, checking, verificationModal } = useClearinghouseVerification({
        workerId: selectedWorker?.id,
        workerName: selectedWorker?.name,
        businessLocationId: businessLocation?.id,
        onProceed: submit,
        onError: setError,
    });

    function submit() {
        if (!selectedWorker) return;
        setError('');
        setPaymentError(undefined);
        setLoading(true);
        client('api/ongoing/create-and-hire/', {
            method: 'POST',
            body: {
                start_date: schedule.startDate,
                end_date: schedule.endDate,
                days_of_week: daysWithinRange,
                times_of_day: buildTimesOfDay(
                    daysWithinRange,
                    schedule.timesMode,
                    schedule.times,
                    schedule.perDayTimes,
                ),
                headline: 'Teacher',
                family_comments: tandemRecommendedDescription,
                preferred_sitters: { preferred_sitters: [{ sitter: selectedWorker.id, spotlight: false }] },
                address_id: address?.id,
                business_location_id: businessLocation?.id,
                family_id: user?.family?.[0]?.id,
                transportation: false,
                is_vpk_classroom: isVpkClassroom,
            },
        })
            .then(() => {
                refresh();
                setOpen(false);
            })
            .catch((saveError) => {
                setLoading(false);
                const payment = (saveError as { payment?: IJobPaymentError })?.payment;
                if (payment) {
                    setPaymentError(payment);
                } else {
                    setError(
                        jobRequestErrorMessage(
                            saveError,
                            'There was an error creating the request. Please try again or contact support.',
                        ),
                    );
                }
            });
    }

    return (
        <Stack gap="md">
            <Title size="h2">Automatically schedule worker</Title>
            {businessLocation?.name && <Text weight="bold">{businessLocation.name}</Text>}
            <Alert variant="warning">
                <Text>
                    <Text weight="bold">
                        <u>The person you select must have already agreed to this shift.</u>
                    </Text>{' '}
                    You must have communicated already with them in person or via another communication medium. If you
                    would like to request a worker instead, please use the &#34;Post Shift&#34; button.
                </Text>
            </Alert>
            <CoverageDateFields
                startDate={schedule.startDate}
                onStartDateChange={schedule.setStartDate}
                endDate={schedule.endDate}
                onEndDateChange={schedule.setEndDate}
                activePreset={schedule.activePreset}
                onPresetSelect={schedule.applyPreset}
                coveredShifts={coveredShifts}
            />
            <DaysTimesFields
                availableDays={schedule.availableDays}
                selectedDays={schedule.selectedDays}
                onSelectedDaysChange={schedule.setSelectedDays}
                timesMode={schedule.timesMode}
                onTimesModeChange={schedule.setTimesMode}
                times={schedule.times}
                onTimesChange={schedule.setTimes}
                perDayTimes={schedule.perDayTimes}
                onDayTimesChange={schedule.setDayTimes}
                showBreak={false}
                showSlots={false}
            />
            <FieldGroup label="Schedule Worker" required>
                <RequestedWorkerFields
                    single
                    placeholder="Search for the worker to schedule"
                    pastWorkers={workers.pastWorkers}
                    preferredWorkers={workers.preferredWorkers}
                    spotlightWorkers={workers.spotlightWorkers}
                    requestedWorkers={selectedWorker ? [selectedWorker] : []}
                    onToggleWorker={toggleWorker}
                />
            </FieldGroup>
            {isVpkLocation && (
                <Checkbox
                    label="Will the worker be in a VPK classroom?"
                    checked={isVpkClassroom}
                    onChange={setIsVpkClassroom}
                />
            )}
            {showUncoveredFeeNotice && (
                <Alert variant="warning">
                    <Stack gap="sm" align="start">
                        <Text size="sm">{uncoveredVpkFeeNotice(feePercent)}</Text>
                        <Checkbox
                            label={uncoveredVpkFeeConfirmLabel}
                            checked={uncoveredFeeAcknowledged}
                            onChange={setUncoveredFeeAcknowledged}
                        />
                    </Stack>
                </Alert>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            <Checkbox
                label={`I have already communicated with ${selectedWorker?.name ?? 'the worker'} and they have agreed to work the entered times.`}
                checked={confirmSelected}
                onChange={setConfirmSelected}
            />
            {paymentError ? <JobPaymentErrorDisplay paymentError={paymentError} /> : null}
            <Row justify="end" align="center" gap="md" fullWidth>
                {disabledReason && (
                    <Text size="sm" tone="secondary">
                        {disabledReason}
                    </Text>
                )}
                <PillButton disabled={loading || checking || !!disabledReason} onClick={submitWithVerification}>
                    {loading || checking ? 'Submitting…' : 'Submit'}
                </PillButton>
            </Row>
            {verificationModal}
        </Stack>
    );
}

export function AutoHireWorkerDialog({
    open,
    setOpen,
    addressId,
    defaultDay,
    refresh,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    addressId?: number;
    defaultDay?: Date;
    refresh: () => void;
}) {
    if (!open) return null;

    return (
        <Modal onClose={() => setOpen(false)} width={643}>
            <AutoHireWorkerDialogContent
                setOpen={setOpen}
                addressId={addressId}
                defaultDay={defaultDay}
                refresh={refresh}
            />
        </Modal>
    );
}
